import React, { useCallback, useEffect, useState } from "react";
import { Widget } from "@typeform/embed-react";
import AuthService from "../auth";
import { getTimeZone, getTimeZoneOffset } from "../utils/dateUtils";
import ConsultScreenerPasswordInput from "../components/onboarding/ConsultScreenerPasswordInput";
import { useActions, useAtom } from "tiny-atom/react/hooks";
import AppointmentScheduler from "../components/appointments/AppointmentScheduler";
import BookedConsult from "../components/appointments/BookedConsult";
import { useGlobalContext } from "../context/GlobalComponentsContext";
import "./Consult.css";
import config from "../config/env";
import { logEvent } from "../actions";
import { reportError } from "../utils/errorUtils";
import ConsultLoading from "../components/onboarding/ConsultLoading";
import { useBeforeunload } from "react-beforeunload";

const TYPEFORM_ID = process.env.REACT_APP_CONSULT_TYPE_FORM_ID;
const CONTINUE_TO_BOOK_CONSULT_QUESTION_REF = "84bc3e6c-5be1-448f-a1d4-75ce0e1f6012";

const qualifiesRef = React.createRef(false);
const accountCreatedRef = React.createRef(false);

const views = {
  typeform: "typeform",
  passwordInput: "passwordInput",
  schedule: "schedule",
  consultBooked: "consultBooked",
  loading: "loading"
};

export default function Consult() {
  const { setConsultView, setCareScreenerSubmissionId } = useActions();
  const [scheduledAppointment, setScheduledAppointment] = useState();
  const { showError } = useGlobalContext();
  const consultView = useAtom(state => state.consultView) || views.typeform;
  const careScreenerSubmissionId = useAtom(state => state.careScreenerSubmissionId);

  useEffect(() => {
    logEvent("careScreenerShown");
  }, []);

  useBeforeunload(consultView !== views.consultBooked ? event => event.preventDefault() : null);

  const handleTypeformSubmitted = useCallback(
    async payload => {
      logEvent("careScreenerSubmission");
      if (payload?.responseId) {
        try {
          const result = await AuthService.createCareScreenerSubmission({
            typeformId: TYPEFORM_ID,
            typeformResponseId: payload.responseId
          });
          setCareScreenerSubmissionId(result.id);
          if (qualifiesRef.current) {
            setConsultView(views.passwordInput);
          }
        } catch (error) {
          reportError(error);
          showError("Something went wrong. Please contact support@getmanatee.com");
        }
      }
    },
    [setCareScreenerSubmissionId, setConsultView, showError]
  );

  const handlePasswordSubmitted = async password => {
    try {
      setConsultView(views.loading);
      await AuthService.completeCareScreener({
        password: password,
        timeZone: getTimeZone(),
        timeZoneOffset: getTimeZoneOffset(),
        careScreenerSubmissionId
      });
      logEvent("careScreenerCompletion");
      accountCreatedRef.current = true;
    } catch (error) {
      setConsultView(views.passwordInput);
      reportError(error);
      showError(error?.message);
    }
  };

  const handleQuestionChanges = useCallback(event => {
    if (event?.ref === CONTINUE_TO_BOOK_CONSULT_QUESTION_REF) {
      qualifiesRef.current = true;
    } else {
      qualifiesRef.current = false;
    }
  }, []);

  const handleAppointmentScheduled = appointment => {
    logEvent("appointmentScheduled");
    setScheduledAppointment(appointment);
    setConsultView(views.consultBooked);
  };

  const handleLoadingTextRotation = useCallback(() => {
    if (accountCreatedRef.current) {
      setConsultView(views.schedule);
    }
  }, [setConsultView]);

  const Typeform = useCallback(() => {
    return (
      <Widget
        onSubmit={handleTypeformSubmitted}
        onQuestionChanged={handleQuestionChanges}
        hidden={{ test: "client-web" }}
        style={{
          height: "90vh",
          width: window.innerWidth < 768 ? 350 : 600,
          boxShadow: "5px 5px 23px -6px rgba(0, 0, 0, 0.25)"
        }}
        id={TYPEFORM_ID}
      />
    );
  }, [handleQuestionChanges, handleTypeformSubmitted]);

  return (
    <div className="consult-container">
      {consultView === views.loading && (
        <div className="column-centered">
          <ConsultLoading onTextRotation={handleLoadingTextRotation} />
        </div>
      )}
      {consultView === views.typeform && (
        <div className="column-centered">
          <Typeform />
        </div>
      )}
      {consultView === views.passwordInput && (
        <div className="column-centered">
          <ConsultScreenerPasswordInput onPasswordSubmitted={handlePasswordSubmitted} />
        </div>
      )}
      {consultView === views.schedule && (
        <AppointmentScheduler
          appointmentId={config.env.REACT_APP_CONSULT_APPT_TYPE_ID}
          appointmentContactMethod={"Phone Call"}
          onAppointmentScheduled={handleAppointmentScheduled}
        />
      )}
      {consultView === views.consultBooked && <BookedConsult appointment={scheduledAppointment} />}
    </div>
  );
}
